import * as React from 'react';
import { usePrevious } from '../../utils/hooks/usePrevious';
import { normalize } from '../../utils/string';
import { Button } from '../Button/Button';
import { ConnectorRow } from '../ConnectorRow/ConnectorRow';
import { SearchBox } from '../SearchBox/SearchBox';
import { Tab } from '../Tab/Tab';
import { CONNECTOR_TABS } from './ConnectorList.types';
import { getConnectorsWithUnifiedSandbox, sortConnectorTabs } from './utils';


var SEARCH_RESULT_EVENT_DELAY_DEFAULT = 600;
var Line = function () { return React.createElement("div", { className: "pluggy line" }); };
export var SUPPORTED_CONNECTOR_TYPES = [
    'PERSONAL_BANK',
    'BUSINESS_BANK',
    'INVESTMENT',
];
var connectorTypeTitleMap = new Map([
    ['PERSONAL_BANK', 'Pessoal'],
    ['BUSINESS_BANK', 'Empresas'],
    ['INVESTMENT', 'Corretoras'],
    ['OTHER', 'Outros'],
]);
var ConnectorListComponent = function (_a) {
    var connectorsProp = _a.connectors, searchBox = _a.searchBox, connectorsSearchQueryProp = _a.search, onSearchChange = _a.onSearchChange, withImages = _a.withImages, searchBoxPlaceholder = _a.searchBoxPlaceholder, _b = _a.searchBoxSize, searchBoxSize = _b === void 0 ? 'small' : _b, missingBankButtonText = _a.missingBankButtonText, searchResultEventDelay = _a.searchResultEventDelay, selectedConnectorTypeProp = _a.selectedConnectorTypeTab, onSelectedConnectorTypeTabChange = _a.onSelectedConnectorTypeTabChange, onMissingBankButtonClick = _a.onMissingBankButtonClick, onSearchClear = _a.onSearchClear, onSelect = _a.onSelect, onSearchResult = _a.onSearchResult, connectorTabsOrder = _a.connectorTabsOrder;
    // group connectors by type
    var connectorsByType = {};
    for (var _i = 0, connectorsProp_1 = connectorsProp; _i < connectorsProp_1.length; _i++) {
        var connector = connectorsProp_1[_i];
        var type = connector.type;
        var connectorsByCurrentType = connectorsByType[type] || [];
        connectorsByCurrentType.push(connector);
        connectorsByType[type] = connectorsByCurrentType;
    }
    var currentConnectorTypes = Object.keys(connectorsByType);
    // connector types available, sorted by SUPPORTED_CONNECTOR_TYPES order
    var currentConnectorTabsFiltered = CONNECTOR_TABS.filter(function (type) {
        return currentConnectorTypes.includes(type) ||
            (type === 'OTHER' &&
                currentConnectorTypes.some(function (type) { return !SUPPORTED_CONNECTOR_TYPES.includes(type); }));
    });
    // order tabs by connectorTabsOrder prop, if specified
    var currentConnectorTabsFilteredOrdered = sortConnectorTabs(currentConnectorTabsFiltered, connectorTabsOrder);
    var initialSelectedConnectorTypeTab = selectedConnectorTypeProp &&
        currentConnectorTabsFilteredOrdered.includes(selectedConnectorTypeProp)
        ? selectedConnectorTypeProp
        : currentConnectorTabsFilteredOrdered[0];
    // connectors type tabs selection
    var _c = React.useState(initialSelectedConnectorTypeTab), selectedConnectorTypeInternal = _c[0], setSelectedConnectorTypeInternal = _c[1];
    // If the prop is undefined, the internal state will be used instead.
    var selectedConnectorTypeTab = selectedConnectorTypeProp || selectedConnectorTypeInternal;
    var tabsRef = Array.from({ length: currentConnectorTabsFilteredOrdered.length }, function () { return React.useRef(null); });
    var handleSelectedConnectorTypeChange = React.useCallback(function (value) {
        var _a, _b, _c;
        if (!selectedConnectorTypeProp) {
            setSelectedConnectorTypeInternal(value);
        }
        onSelectedConnectorTypeTabChange === null || onSelectedConnectorTypeTabChange === void 0 ? void 0 : onSelectedConnectorTypeTabChange(value);
        var tabPosition = ((_a = tabsRef[currentConnectorTabsFilteredOrdered.indexOf(value)].current) === null || _a === void 0 ? void 0 : _a.offsetLeft) || 0;
        var centerPosition = (((_b = connectorTabsRef.current) === null || _b === void 0 ? void 0 : _b.scrollWidth) || 0) / 2;
        var distanceFromCenter = Math.abs(centerPosition - tabPosition);
        var scrollTo = tabPosition > centerPosition
            ? tabPosition + distanceFromCenter
            : tabPosition - distanceFromCenter;
        (_c = connectorTabsRef.current) === null || _c === void 0 ? void 0 : _c.scrollTo({
            left: scrollTo,
            behavior: 'smooth',
        });
    }, [selectedConnectorTypeProp, onSelectedConnectorTypeTabChange]);
    // search
    var initialConnectorsSearchQuery = connectorsSearchQueryProp || '';
    var _d = React.useState(initialConnectorsSearchQuery), connectorsSearchQueryInternal = _d[0], setConnectorsSearchQueryInternal = _d[1];
    var connectorsSearchQuery = connectorsSearchQueryProp !== undefined
        ? connectorsSearchQueryProp
        : connectorsSearchQueryInternal;
    var _e = React.useState(), connectorsFiltered = _e[0], setConnectorsFiltered = _e[1];
    var previousSearchResultQuery = React.useRef();
    var previousSearchResultConnectorTypeTab = React.useRef();
    var connectorTabsRef = React.useRef(null);
    var submitSearchResult = React.useCallback(function () {
        var selectedTabConnectors = selectedConnectorTypeTab === 'OTHER'
            ? connectorsProp.filter(function (connector) { return !SUPPORTED_CONNECTOR_TYPES.includes(connector.type); })
            : connectorsByType[selectedConnectorTypeTab];
        var connectorsByCurrentConnectorType = getConnectorsWithUnifiedSandbox(selectedTabConnectors || []);
        var connectorsFilteredResult = connectorsSearchQuery === ''
            ? // empty search -> no filtering
                connectorsByCurrentConnectorType
            : // filter by search query, checking connector.name, ignoring case/accents
                connectorsByCurrentConnectorType.filter(function (connector) {
                    var connectorNameNormalized = normalize(connector.name);
                    var inputSearchNormalized = normalize(connectorsSearchQuery);
                    return connectorNameNormalized.includes(inputSearchNormalized);
                });
        setConnectorsFiltered(connectorsFilteredResult);
        onSearchResult === null || onSearchResult === void 0 ? void 0 : onSearchResult({
            searchQuery: connectorsSearchQuery,
            previousSearchQuery: previousSearchResultQuery.current,
            selectedConnectorTypeTab: selectedConnectorTypeTab,
            previousSelectedConnectorTypeTab: previousSearchResultConnectorTypeTab.current,
            connectorsResult: connectorsFilteredResult,
        });
        return;
    }, [
        connectorsByType,
        connectorsSearchQuery,
        onSearchResult,
        connectorsFiltered,
    ]);
    var handleSearchBoxChange = React.useCallback(function (event) {
        var newSearchQuery = event.target.value;
        if (connectorsSearchQueryProp === undefined) {
            setConnectorsSearchQueryInternal(newSearchQuery);
        }
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange(newSearchQuery);
    }, [connectorsSearchQueryProp, onSearchChange]);
    var handleClear = React.useCallback(function () {
        if (connectorsSearchQueryProp === undefined) {
            setConnectorsSearchQueryInternal('');
        }
        onSearchChange === null || onSearchChange === void 0 ? void 0 : onSearchChange('');
        onSearchClear === null || onSearchClear === void 0 ? void 0 : onSearchClear();
    }, [onSearchClear, connectorsSearchQueryProp, onSearchChange]);
    var searchResultEventTimeout = React.useRef();
    var previousSearch = usePrevious(connectorsSearchQuery);
    var previousSelectedConnectorType = usePrevious(selectedConnectorTypeTab);
    React.useEffect(function () {
        // check if window is defined to prevent from breaking in SSR
        if (typeof window === 'undefined') {
            return;
        }
        var isFirstSearch = connectorsFiltered === undefined;
        var isSearchQueryChange = connectorsSearchQuery !== previousSearch;
        var isConnectorTypeChange = selectedConnectorTypeTab !== previousSelectedConnectorType;
        if (!isSearchQueryChange && !isConnectorTypeChange) {
            // search query nor tabs didn't change, no update
            return;
        }
        // clear previously scheduled search
        clearTimeout(searchResultEventTimeout.current);
        // save previous state for search result report
        previousSearchResultConnectorTypeTab.current = previousSelectedConnectorType;
        previousSearchResultQuery.current = previousSearch;
        var isOnlyConnectorTypeChange = !isSearchQueryChange && isConnectorTypeChange;
        var isEmptySearchQuery = connectorsSearchQuery === '';
        var isImmediateSearch = isFirstSearch || isEmptySearchQuery || isOnlyConnectorTypeChange;
        if (isImmediateSearch) {
            // don't delay if it's first search, or search query is empty, or if only changed type tab
            submitSearchResult();
            return;
        }
        var searchDelay = searchResultEventDelay || SEARCH_RESULT_EVENT_DELAY_DEFAULT;
        searchResultEventTimeout.current = window.setTimeout(submitSearchResult, searchDelay);
        return function () {
            clearTimeout(searchResultEventTimeout.current);
        };
    }, [
        connectorsFiltered,
        connectorsSearchQuery,
        previousSearch,
        selectedConnectorTypeTab,
        previousSelectedConnectorType,
        submitSearchResult,
        searchResultEventDelay,
    ]);
    var classes = "pluggy connector-list";
    var smallTabs = currentConnectorTabsFilteredOrdered.length > 3;
    return (React.createElement("div", { className: classes },
        searchBox && (React.createElement(SearchBox, { value: connectorsSearchQuery, onChange: handleSearchBoxChange, onClear: handleClear, placeholder: searchBoxPlaceholder, size: searchBoxSize })),
        currentConnectorTabsFilteredOrdered.length > 1 && (React.createElement("div", { className: "connector-tabs " + (smallTabs ? 'small-tabs' : ''), ref: connectorTabsRef }, currentConnectorTabsFilteredOrdered.map(function (connectorType, index) { return (React.createElement("div", { ref: tabsRef[index], className: 'tab-container' },
            React.createElement(Tab, { key: connectorType, title: connectorTypeTitleMap.get(connectorType) || connectorType, onClick: function () { return handleSelectedConnectorTypeChange(connectorType); }, active: selectedConnectorTypeTab === connectorType }))); }))),
        React.createElement("div", { className: "connector-rows" },
            React.createElement("div", { className: "connector-container" }, connectorsFiltered !== undefined && connectorsFiltered.length > 0 ? (connectorsFiltered.map(function (connector, i) { return (React.createElement(React.Fragment, { key: i },
                React.createElement(ConnectorRow, { connector: connector, onClick: function () {
                        var _a;
                        return ((_a = connector.health) === null || _a === void 0 ? void 0 : _a.status) !== 'OFFLINE' && (onSelect === null || onSelect === void 0 ? void 0 : onSelect(connector));
                    }, withImage: withImages }),
                i !== connectorsFiltered.length - 1 && React.createElement(Line, null))); })) : (React.createElement("div", { className: "empty-result" },
                React.createElement("p", null,
                    "Nenhum resultado para \u201C",
                    connectorsSearchQuery,
                    "\u201D"),
                React.createElement("a", { onClick: handleClear }, "Fa\u00E7a outra tentativa")))),
            missingBankButtonText && (React.createElement("div", { className: "missing-bank-button" },
                React.createElement(Button, { onClick: onMissingBankButtonClick, link: true }, missingBankButtonText))))));
};
var ConnectorList = React.memo(ConnectorListComponent);
export { CONNECTOR_TABS, ConnectorList, };
